import { toast } from 'sonner';
import apiClient from '@/utils/apiClient';

/**
 * Helper to convert Blob to Base64 data string (excluding data URI prefix).
 */
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = (err) => reject(err);
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = typeof dataUrl === 'string' ? dataUrl.split(',')[1] : '';
      resolve(base64);
    };
    reader.readAsDataURL(blob);
  });
};

/**
 * Determine MIME type from filename extension if not provided.
 */
const getMimeTypeFromFilename = (fileName, fallback = 'application/octet-stream') => {
  if (!fileName) return fallback;
  const ext = fileName.split('.').pop()?.toLowerCase();
  const mimeMap = {
    pdf: 'application/pdf',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    csv: 'text/csv',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    doc: 'application/msword',
    zip: 'application/zip',
    json: 'application/json',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
    txt: 'text/plain',
    html: 'text/html'
  };
  return mimeMap[ext] || fallback;
};

/**
 * Universal Download Function for Reports, PDFs, Excel, Media, and static files.
 * Supports Web Browser and Native Mobile App (Flutter WebView & Capacitor).
 * 
 * @param {Blob|ArrayBuffer|string} fileData - The file content (Blob, ArrayBuffer, base64 data URI) or direct URL string
 * @param {string} fileName - Destination filename (e.g. 'Report_Card.pdf')
 * @param {string} mimeType - Optional MIME type
 */
export const downloadFile = async (fileData, fileName = 'download', mimeType = null) => {
  try {
    const effectiveMimeType = mimeType || getMimeTypeFromFilename(fileName);

    // 1. Resolve raw data to a Blob if it is a remote/relative URL
    let blob = null;
    let base64Data = null;

    if (typeof fileData === 'string') {
      if (fileData.startsWith('data:')) {
        base64Data = fileData.split(',')[1];
      } else if (fileData.startsWith('http://') || fileData.startsWith('https://') || fileData.startsWith('/')) {
        // Fetch URL as a blob using apiClient to retain auth headers
        let fetchUrl = fileData;
        if (fetchUrl.startsWith('/api/')) {
          fetchUrl = fetchUrl.substring(4); // apiClient baseURL already includes /api
        }
        const response = await apiClient.get(fetchUrl, { responseType: 'blob' });
        blob = response.data;
      } else if (fileData.startsWith('blob:')) {
        const response = await fetch(fileData);
        blob = await response.blob();
      } else {
        // Plain text or CSV content
        blob = new Blob([fileData], { type: effectiveMimeType });
      }
    } else if (fileData instanceof Blob) {
      blob = fileData;
    } else {
      // ArrayBuffer or typed array
      blob = new Blob([fileData], { type: effectiveMimeType });
    }

    // 2. Check for Flutter Native Mobile App Bridge
    const isFlutterApp = Boolean(
      window.DormCircleNative ||
      window.isDormCircleMobileApp ||
      window.dormCircleFcmToken ||
      (navigator.userAgent && navigator.userAgent.includes('DormCircleApp'))
    );

    if (isFlutterApp && window.DormCircleNative) {
      if (!base64Data && blob) {
        base64Data = await blobToBase64(blob);
      }

      window.DormCircleNative.postMessage(JSON.stringify({
        action: 'DOWNLOAD_FILE',
        fileName: fileName,
        base64Data: base64Data,
        mimeType: effectiveMimeType
      }));

      toast.success(`Downloading ${fileName}...`);
      return;
    }

    // 3. Check for Capacitor Native Mobile App
    const isCapacitor = Boolean(window.Capacitor?.isNativePlatform && window.Capacitor.isNativePlatform());
    const hasNativeFilesystem = Boolean(window.Capacitor?.Plugins?.Filesystem);

    if (isCapacitor && hasNativeFilesystem) {
      const Filesystem = window.Capacitor.Plugins.Filesystem;
      if (!base64Data && blob) {
        base64Data = await blobToBase64(blob);
      }

      const savedFile = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: 'DOCUMENTS',
        recursive: true
      });

      toast.success(`Saved to Documents: ${fileName}`);

      if (window.Capacitor?.Plugins?.FileOpener) {
        try {
          await window.Capacitor.Plugins.FileOpener.open({
            filePath: savedFile.uri,
            contentType: effectiveMimeType
          });
        } catch (openErr) {
          console.log('File saved, opener error:', openErr);
        }
      }
      return;
    }

    // 4. Standard Web Browser Download
    let blobUrl = '';
    if (blob) {
      blobUrl = window.URL.createObjectURL(blob);
    } else if (base64Data) {
      blobUrl = `data:${effectiveMimeType};base64,${base64Data}`;
    }

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();

    if (blobUrl.startsWith('blob:')) {
      // Delay revoke by 30 seconds to avoid breaking async readers
      setTimeout(() => {
        try {
          window.URL.revokeObjectURL(blobUrl);
        } catch (e) {}
      }, 30000);
    }

    toast.success(`Downloaded: ${fileName}`);
  } catch (error) {
    console.error('Download error:', error);
    toast.error('Failed to download file.');
  }
};

export default downloadFile;
